import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLayoutEffect } from "react";
import {
  Users,
  Award,
  Sparkles,
  Star,
  BookOpen,
  Laptop,
  Network,
  ArrowRight,
  Zap,
  Trophy,
  Target,
} from "lucide-react";

const Alumni = () => {
  useLayoutEffect(() => { window.scrollTo(0, 0); }, []);
  const benefitPairs = [
    {
      front: {
        title: "Network",
        icon: Network,
        gradient: "from-red-500 to-rose-600",
        items: [
          "Join location-based alumni groups",
          "Participate in regular meetups to share ideas & collaborate",
        ],
      },
      back: {
        title: "Events",
        icon: Sparkles,
        gradient: "from-green-500 to-emerald-600",
        items: [
          "Annual Alumni gathering during Udbhav Demo Day",
          "Monthly online sessions & knowledge sharing",
          "Connect with innovators across all cohorts",
        ],
      },
    },
    {
      front: {
        title: "Mentorship",
        icon: Award,
        gradient: "from-red-500 to-rose-600",
        items: [
          "Guide teams participating in the next Udbhav edition",
          "Improve solution quality, teamwork & innovation readiness",
        ],
      },
      back: {
        title: "Alumni Portal",
        icon: Laptop,
        gradient: "from-green-500 to-emerald-600",
        items: [
          "A unified portal for chapters & profiles",
          "Search, connect & collaborate with alumni",
          "A long-term space for updates & engagement",
        ],
      },
    },
  ];

  const immediateBenefits = [
    {
      title: "Regional Chapters",
      desc: "Start or join local chapters and coordinate activities",
      icon: Target,
    },
    {
      title: "Mentor Udbhav Participants",
      desc: "Support new teams through the project journey",
      icon: Users,
    },
    {
      title: "Collaboration Opportunities",
      desc: "Work with finalists to strengthen implementations",
      icon: Network,
    },
    {
      title: "Monthly Knowledge Sessions",
      desc: "Share or learn from real project experiences",
      icon: BookOpen,
    },
    {
      title: "Exclusive Training",
      desc: "Access special workshops & programs for alumni",
      icon: Trophy,
    },
  ];

  return (
    <div className="min-h-screen py-20 px-6 bg-gradient-to-b from-gray-950 via-black to-gray-900 text-gray-100 overflow-hidden relative">
      {/* Background Effects */}
      <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-red-600/10 blur-[200px] rounded-full animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-green-500/10 blur-[200px] rounded-full animate-pulse"></div>
      <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-red-500/5 blur-[150px] rounded-full"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* ================= HEADER ================= */}
        <div className="text-center mb-24">
          {/* <div className="relative w-32 h-32 mx-auto mb-10">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-green-500 to-red-500 rounded-full blur-xl opacity-60 animate-pulse"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-green-600 rounded-full shadow-[0_0_60px_rgba(220,38,38,0.5)]"></div>
            <div className="absolute inset-[4px] bg-gradient-to-br from-gray-900 to-black rounded-full flex items-center justify-center">
              <Users className="w-14 h-14 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)] animate-bounce" />
            </div>
          </div> */}

          {/* <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-green-400 to-red-500 tracking-tight mb-6 animate-gradient">
            Udbhav Alumni Platform
          </h1> */}

          <p className="text-2xl font-bold  text-gray-300 max-w-3xl mx-auto text-center mt-6">
            Unite with past finalists in a vibrant, future-ready innovation
            community. Drive transformation through{" "}
            <span className="text-green-400 font-semibold">collaboration</span>,{" "}
            <span className="text-red-400 font-semibold">lifelong learning</span>
            , and{" "}
            <span className="text-green-400 font-semibold">leadership</span>.
          </p>
        </div>

        {/* ================= WHAT YOU GET SECTION ================= */}
        <div className="mb-28">
<h2 className="text-3xl text-center font-bold mb-12
  bg-gradient-to-r from-red-500 via-green-500 to-red-500 bg-clip-text text-transparent">
  What You Get as an Alumnus
</h2>


<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-7xl mx-auto">
  {benefitPairs.map((pair, index) => (
    <React.Fragment key={index}>
      {/* FRONT CARD */}
      <Card className="relative bg-gradient-to-br from-gray-900/80 to-black/90 backdrop-blur-xl border border-gray-800 rounded-2xl shadow-md overflow-hidden min-h-[300px]">
        <CardContent className="p-6 flex flex-col h-full">
          <div className={`w-16 h-16 mb-4 mx-auto rounded-2xl bg-gradient-to-br ${pair.front.gradient} p-[2px] shadow-lg`}>
            <div className="w-full h-full bg-gray-900 rounded-2xl flex items-center justify-center">
              <pair.front.icon className="w-8 h-8 text-white" />
            </div>
          </div>

          <h3 className={`text-xl font-bold text-center mb-4 text-transparent bg-clip-text bg-gradient-to-br ${pair.front.gradient}`}>
            {pair.front.title}
          </h3>

          <ul className="text-gray-300 space-y-2 text-sm mt-auto">
            {pair.front.items.map((x, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="text-green-400 text-lg mt-0.5">•</span>
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* BACK CARD */}
      <Card className="relative bg-gradient-to-br from-gray-900/80 to-black/90 backdrop-blur-xl border border-gray-800 rounded-2xl shadow-md overflow-hidden min-h-[300px]">
        <CardContent className="p-6 flex flex-col h-full">
          <div className={`w-16 h-16 mb-4 mx-auto rounded-2xl bg-gradient-to-br ${pair.back.gradient} p-[2px] shadow-lg`}>
            <div className="w-full h-full bg-gray-900 rounded-2xl flex items-center justify-center">
              <pair.back.icon className="w-8 h-8 text-white" />
            </div>
          </div>

          <h3 className={`text-xl font-bold text-center mb-4 text-transparent bg-clip-text bg-gradient-to-br ${pair.back.gradient}`}>
            {pair.back.title}
          </h3>

          <ul className="text-gray-300 space-y-2 text-sm mt-auto">
            {pair.back.items.map((x, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="text-red-400 text-lg mt-0.5">•</span>
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </React.Fragment>
  ))}
</div>


        </div>
<div className="mb-20 flex flex-col items-center">
  <h2 className="text-3xl text-center font-bold bg-gradient-to-r from-red-500 via-green-500 to-red-500 bg-clip-text text-transparent">
    Immediate Benefits for Finalists
  </h2>
  <p className="text-center text-gray-400 text-lg mb-12">
    Unlock exclusive opportunities and resources designed specifically
    for our alumni network
  </p>

  {/* Horizontal scrolling row */}
  <div className="flex gap-6 overflow-x-auto py-4 px-2">
    {[
      "Regional Chapters",
      "Collaboration Opportunities",
      "Exclusive Training",
      "Mentor Udbhav",
      "Monthly Knowledge Sessions",
    ].map((title, i) => (
      <div
        key={i}
        className={`flex-shrink-0 flex items-center justify-center gap-3 bg-gray-900/80 border border-gray-800 rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 animate-slide-in`}
        style={{ animationDelay: `${i * 0.2}s` }} // staggered delay
      >
        <span className="text-white font-medium">{title}</span>
      </div>
    ))}
  </div>
</div>



{/* ================= DIRECTORY ================= */}
{/* ================= DIRECTORY ================= */}
{/* ================= DIRECTORY ================= */}
<div className="mb-20">
  <h2 className="text-2xl md:text-2xl font-bold text-center mb-10 text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-green-400 to-red-500">
  Udbhav Alumni Program (प्रवाह) – Expression of Interest

  </h2>

  <Card
    className="relative border-2 border-red-600/40 bg-gradient-to-br from-gray-900/80 via-black to-gray-900/80 
    py-8 px-4 md:px-8 flex flex-col items-center justify-center 
    hover:shadow-[0_0_40px_rgba(220,38,38,0.3),0_0_70px_rgba(34,197,94,0.2)] 
    transition-all duration-500 overflow-hidden rounded-2xl group"
  >
    {/* Background gradient overlay */}
    <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 via-green-600/10 to-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

    {/* Glow orb */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
      w-[300px] h-[300px] bg-gradient-to-r from-red-500/20 to-green-500/20 
      rounded-full blur-[100px] group-hover:blur-[140px] transition-all duration-700">
    </div>

    {/* Content */}
    <div className="relative z-10 w-full max-w-5xl">

      <p className="text-gray-300 text-center mb-6 max-w-3xl mx-auto">
        Welcome to <span className="font-semibold">प्रवाह</span>, the Udbhav Alumni Program — a
        community of innovators, problem-solvers, and change leaders who were part of Udbhav’s journey.
      </p>

      {/* Google Form Embed */}
      <div className="w-full rounded-xl overflow-hidden border border-gray-700 bg-black/40">
        <iframe
          src="https://forms.gle/QyLreuR3Ei1s8NVNA"
          title="Udbhav Alumni Program – Expression of Interest"
          className="w-full h-[900px]"
          style={{ border: "none" }}
          loading="lazy"
        />
      </div>
    </div>
  </Card>
</div>


      </div>

      {/* ================= STYLES ================= */}
      <style jsx>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }
        .shimmer-effect {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          animation: shimmer 2s infinite;
        }

        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .perspective-1000 {
          perspective: 1000px;
        }
        .flip-card {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.8s;
          transform-style: preserve-3d;
        }
        .flip-card-container:hover .flip-card {
          transform: rotateY(180deg);
        }
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
        }
        .flip-card-back {
          transform: rotateY(180deg);
        }
      `}</style>
    </div>
  );
};

export default Alumni;
